/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Element } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import AttachmentUploader from '../uploader/AttachmentUploader';
import { useAttachmentsField } from './useAttachmentsField';
import UploadedAttachments from '../uploaded/UploadedAttachments';
import { filterAttachmentsFormData, mapDroppedFilesToFormData } from '../services/attachments.helper';
import { uiSchemaProperties } from '../../../../shared/enums/uiSchemaProperties';
import { attachmentTypes as generalAttachmentTypes, ATTACHMENT_TYPE_META_FIELDS } from '../services/constants';
import AttachmentComplianceInfo from './AttachmentComplianceInfo';
import DrawingsComplianceInfo from './DrawingsComplianceInfo';
import { useAttachmentMetaFields } from '../uploader/meta/useAttachmentMetaFields';
import { ConfigResourceProps } from '../../../../redux/enums/ConfigResourceProps';
import { useConfiguration } from '../../../../redux/features/configuration/useConfiguration';

const useStyles = makeStyles(theme => ({
  sectionHidden: {
    visibility: 'hidden',
    opacity: 0,
    height: 0,
    transition: 'visibility 0s, opacity 0.5s linear',
  },
  whitePaper: {
    marginBottom: '4rem',
    padding: '1rem 1rem',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.white,
    boxShadow: '0rem 0.1rem 0.2rem rgba(63, 63, 68, 0.15)',
    borderRadius: '0.2rem',
    width: '100%',
    maxWidth: '60rem',
  },
}));

/**
 * Custom field component to handle the attachments. It can handle multiple attachment types with separate components
 * for each attachment type.
 * It requires uiSchema setup to be fully functional. E.g.:
 *
 *   "attachments": {
 *     "ui:field": "AttachmentsField",
 *     "ui:title": "i18n(attachments.title)",
 *     "ui:infoText": "i18n(attachments.info.text)",
 *     "ui:icon": "attach_file",
 *     "ui:disabled": false,
 *     "scrollSection": true,
 *     "attachments:categories": [ "TECHNICAL", "ADDITIONAL" ],
 *     "attachments:showComplianceInfo": true
 *   }
 *
 * @param props - form related props injected by the RJSF
 * @returns {JSX.Element}
 * @constructor
 */
export default function AttachmentsField(props) {
  const { idSchema, disabled, registry, name, uiSchema } = props;
  const { submissionId, classes = {}, schema } = registry.formContext;
  const ObjectField = registry?.fields?.ObjectField;
  const { t } = useTranslation();
  const localClasses = useStyles();
  const { extractAllDropdownNames } = useAttachmentMetaFields();

  const disabledAttachmentTypes = uiSchema?.['ui:options']?.disabledAttachmentTypes || [];
  const attachmentsKey = uiSchema[uiSchemaProperties.ATTACHMENTS_OVERWRITTEN_FORM_KEY] ?? name;
  const showComplianceInfo = uiSchema[uiSchemaProperties.ATTACHMENTS_SHOW_COMPLIANCE_INFO];
  const showDrawingsComplianceInfo = uiSchema[uiSchemaProperties.ATTACHMENTS_SHOW_DRAWINGS_COMPLIANCE_INFO];
  const showCustomTooltips = uiSchema[uiSchemaProperties.ATTACHMENTS_CUSTOM_TOOLTIPS];
  const attachmentsFormData = registry.getFormData(attachmentsKey) ?? [];

  const {
    attachmentTypes,
    showUserSpecificAttachments,
    isSectionHidden,
    dropZoneFiles,
    onFileDropped,
    onDroppedFileRemoved,
    onAttachmentUploaded,
    onUploadedAttachmentRemoved,
    getCustomTooltipByProcedure,
  } = useAttachmentsField({
    submissionId,
    attachmentsFormData,
    registry,
    uiSchema,
    idSchema,
    attachmentsKey,
  });

  const className = classNames(classes.sectionTopIndent, isSectionHidden && localClasses.sectionHidden);
  const customTooltip = getCustomTooltipByProcedure(showCustomTooltips);
  const frontendCustomisationConfig = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);

  return (
    <>
      <Element name={idSchema.$id} className={className}>
        <ObjectField {...props} />
      </Element>

      {showComplianceInfo && <AttachmentComplianceInfo />}

      {showDrawingsComplianceInfo && <DrawingsComplianceInfo />}

      {customTooltip && <span className={classes.customTooltip}>{t(customTooltip)}</span>}

      {attachmentTypes.map((attachmentType, index) => {
        const userId = showUserSpecificAttachments ? registry.getFormData('lockUser') : undefined;
        // allow attachments form data to be given as input (bioMaterials exception)
        const attachmentsFormDataForType =
          props.attachmentsMetadata ?? filterAttachmentsFormData(attachmentsFormData, attachmentType, userId);
        const dropZoneFileMetadata = mapDroppedFilesToFormData(dropZoneFiles);
        const dropZoneFilesMetadataForType = filterAttachmentsFormData(dropZoneFileMetadata, attachmentType);
        let metaFieldNames =
          ATTACHMENT_TYPE_META_FIELDS[
            attachmentType === frontendCustomisationConfig?.resource?.customSA
              ? generalAttachmentTypes.SA
              : attachmentType
          ] || [];
        metaFieldNames = metaFieldNames.concat(extractAllDropdownNames(attachmentType, schema));

        return (
          <div key={index}>
            <AttachmentUploader
              draftId={submissionId}
              attachmentType={attachmentType}
              metaFieldNames={metaFieldNames}
              titleLabel={t(`attachments.uploader.title.label.${attachmentType.toLowerCase()}`)}
              subtitleLabel={t(`attachments.uploader.subtitle.label.${attachmentType.toLowerCase()}`)}
              selectorLabel={t(`attachments.uploader.selector.label.${attachmentType.toLowerCase()}`)}
              disabledAttachmentTypes={disabledAttachmentTypes}
              attachmentsMetadata={attachmentsFormDataForType}
              mergedAttachmentsMetadata={attachmentsFormDataForType.concat(dropZoneFilesMetadataForType)}
              onFileDropped={onFileDropped}
              onDroppedFileRemoved={onDroppedFileRemoved}
              onAttachmentUploaded={props.onAttachmentUploaded ?? onAttachmentUploaded}
              uiSchema={uiSchema}
              jsonSchema={schema}
              parentClasses={{ ...classes, ...localClasses }}
              readOnly={disabled}
            />

            <UploadedAttachments
              draftId={submissionId}
              title={t(`attachments.uploaded.title.label.${attachmentType.toLowerCase()}`)}
              attachmentType={attachmentType}
              attachments={attachmentsFormDataForType}
              parentClasses={{ ...classes, ...localClasses }}
              onDeleteFile={props.onUploadedAttachmentRemoved ?? onUploadedAttachmentRemoved}
              readOnly={disabled}
              schema={schema}
            />
          </div>
        );
      })}
    </>
  );
}
